/**
 * @fileoverview Feature flags — the client engine.
 * @module lib/features/core/flags
 *
 * The whole integration:
 *
 * ```ts
 * import { flags } from 'flowgrid-sdk';
 *
 * if (flags.on('new-checkout')) {
 *   renderNewCheckout();
 * }
 * ```
 *
 * Three rules shape everything below, and each one removes a step that other
 * flag SDKs make you take:
 *
 * 1. **`on()` is synchronous.** Rules are already in memory, so a flag read is a
 *    map lookup. An `await` on a render path is what makes flags feel heavy, and
 *    it is why people end up caching them badly by hand.
 * 2. **The second argument is the default, not an options bag.** `on('x', true)`
 *    reads as "on unless told otherwise". Network down, flag never created, SDK
 *    not initialised — you get your default. Never an exception, never a hang.
 * 3. **No registration step.** Reading an unknown key returns the default *and*
 *    reports the key, so it shows up in the dashboard as "seen in the wild" with
 *    a one-click Create. Write the code first; the dashboard catches up.
 *
 * Evaluation is local, against a bundle fetched once per page load and cached in
 * localStorage, so a returning visitor gets correct answers before the network
 * responds — and correct-ish answers with no network at all.
 */

import {
  loadOverrides,
  overrideDecision,
  writeStoredOverrides,
  type FlagOverride,
  type FlagOverrides,
} from './flagOverrides';
import { FlowGridTransport } from '../../client/transport';
import { SDK_VERSION } from '../../version';

import { detectEnvironment } from './flagEnvironment';
import {
  bundleBlocksPath,
  decideFlag,
  evaluateFlag,
  type FlagBundle,
  type FlagContext,
  type FlagInputContext,
  type FlagDecision,
  type FlagDelivery,
  type FlagVariantValue,
} from './flagEval';
import {
  missingFrom,
  normaliseDeclarations,
  sendDeclarations,
  type FlagDeclaration,
  type NormalisedDeclaration,
} from './flagDeclare';
import { runFlagged, toFlagErrorPayload } from './flagErrors';

// The evaluator lives in ./flagEval so it can be imported without this file's
// browser transport — see the note there. Re-exported for convenience.
export {
  evaluateFlag,
  decideFlag,
  type FlagBundle,
  type FlagContext,
  type FlagDecision,
  type FlagDelivery,
  type FlagVariantValue,
  type FlagType,
  type CompiledFlag,
  type CompiledRule,
  type FlagRuleType,
  type FlagRuleOperator,
} from './flagEval';
export { environmentForHost, detectEnvironment, type FlagEnvironment } from './flagEnvironment';
export type { FlagDeclaration } from './flagDeclare';

// ============================================================================
// THE ENGINE
// ============================================================================

const STORAGE_KEY = (webId: string) => `fg_flags_${webId}`;

/** How long a cached bundle is used before it is considered untrustworthy. */
const STORAGE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Where a persisted bundle sits in the answer order.
 *
 * The engine has always done one of these — persistence first, replaced by the
 * network when it lands. Naming the other two costs almost nothing and settles
 * an argument the default cannot settle for everybody: a stale answer on the
 * first frame is a bargain for a marketing page and unacceptable for a pricing
 * page, and only the app knows which one it is.
 *
 * - `persistenceUntilNetworkSuccess` — **default.** The stored bundle answers
 *   immediately, including on the very first render, and stays the answer until
 *   a fetch succeeds. No flash of the default; possibly stale for one round
 *   trip.
 * - `networkFirst` — reads before the fetch settles get the caller's own
 *   fallback. The stored bundle is adopted only if the fetch *fails*, so
 *   persistence is a safety net rather than a first answer.
 * - `networkOnly` — persistence is never read and never written. A failed fetch
 *   means every flag is the caller's fallback, and closing the tab forgets
 *   everything.
 *
 * Two consequences of leaving the default are worth stating, because they are
 * why it is the default: a returning visitor evaluates correctly with the
 * network off, and a blocked route costs no request at all (the local bundle is
 * what the path is checked against). `networkOnly` gives up both — every load on
 * a blocked route goes to core for its `204`.
 */
export type FlagLookupPolicy =
  | 'persistenceUntilNetworkSuccess'
  | 'networkFirst'
  | 'networkOnly';

/** The same three, at runtime — a bundle's policy is parsed JSON, not a type. */
const LOOKUP_POLICIES: readonly FlagLookupPolicy[] = [
  'persistenceUntilNetworkSuccess',
  'networkFirst',
  'networkOnly',
];

/** Delivery options — how answers are fetched and kept, not what they mean. */
export interface FlagEngineOptions {
  /** See {@link FlagLookupPolicy}. Default `persistenceUntilNetworkSuccess`. */
  lookupPolicy?: FlagLookupPolicy;

  /**
   * How long a persisted bundle stays usable, in milliseconds. Default 24h.
   *
   * Past it the stored copy is discarded rather than served: a flag turned off a
   * week ago must not come back to life on a cache. Lower it if you would rather
   * flash a default than serve a day-old answer; there is no upper clamp, but a
   * TTL longer than your release cadence is a decision to ship stale flags.
   */
  persistenceTtlMs?: number;

  /**
   * Flags this code reads, so they exist in the dashboard without anyone
   * creating them there. A key the website does not have yet is created **off**
   * on the next load — declaring never turns a flag on.
   *
   * ```ts
   * Flowgrid.init({ webId, flags: { declare: ['new-checkout', { key: 'beta-banner', description: 'Top banner' }] } });
   * ```
   */
  declare?: FlagDeclaration[];

  /**
   * Honour `?fg_flags=key:variant` on this website's public pages. Off by
   * default: on a public page, anyone can type a query string, and a flag that
   * gates a paid feature must not be switchable from the address bar. Local
   * hosts always honour it, and `flags.override()` in your own code always works.
   */
  allowUrlOverrides?: boolean;
}

/**
 * Where an answer physically came from — the delivery fact, not the reasoning.
 *
 * `reason` on a {@link FlagDecision} says which *step of the rules* decided
 * (kill switch, a rule, the default). This says which *copy of the bundle* that
 * step ran against. Both are needed to explain a wrong answer, and neither
 * implies the other: `reason: 'default'` off a day-old persisted bundle and
 * `reason: 'default'` off a bundle fetched a second ago are the same reasoning
 * and completely different bugs.
 *
 * - `network` — the bundle was fetched this page load.
 * - `persistence` — the bundle came from localStorage; no fetch has replaced it.
 * - `resolve` — the server answered this key directly, because its rules were
 *   withheld from the browser on purpose. Network, but not the bundle.
 * - `fallback` — there was no answer here at all and the caller's default was
 *   returned. Unknown key, nothing loaded, or flag delivery off for this route.
 * - `override` — forced by `flags.override()` or `?fg_flags=`; never counted.
 * - `unknown` — an engine we do not own answered (`setFlagEngine`, a
 *   `FlagSource`, an SSR seed) and did not say where from. Reported rather than
 *   guessed at: a source field whose value is sometimes invented is worse for
 *   debugging than one that admits it does not know.
 */
export type FlagAnswerSource =
  | 'network'
  | 'persistence'
  | 'resolve'
  | 'fallback'
  | 'override'
  | 'unknown';

/**
 * A decision with its delivery attached — what `flags.explain(key)` returns.
 *
 * A widening of {@link FlagDecision}, so anything already reading `value`,
 * `variant`, `payload` or `reason` off an `explain()` keeps working.
 */
export interface FlagReading extends FlagDecision {
  source: FlagAnswerSource;
}

/** How long to wait for more unknown keys before asking the server about them. */
const RESOLVE_DEBOUNCE_MS = 50;

/**
 * How long to collect exposures before sending them.
 *
 * Long enough that a page rendering twenty flags in one paint costs one request,
 * short enough that an exposure lands before the conversion it is meant to
 * explain. A flag read and a purchase thirty seconds later must arrive in that
 * order, or the join that makes an experiment readable has nothing to join to.
 */
const EXPOSURE_DEBOUNCE_MS = 2000;

/**
 * Cap on distinct exposures held for one session.
 *
 * An exposure is deduped per flag per arm, so reaching this means a page is
 * reading hundreds of distinct flags — pathological, and not a reason to grow a
 * browser-side Set without limit.
 */
const MAX_SESSION_EXPOSURES = 500;

/**
 * Fraction of sessions that report a usage digest.
 *
 * Usage counts answer "is this flag still read anywhere" — the question you have
 * to answer before deleting a flag, and the one flag systems die without. It
 * does not need to be exact, and a digest from every session of every site would
 * be a lot of rows to learn something a 1-in-10 sample already tells us.
 */
const USAGE_SAMPLE_RATE = 0.1;

interface StoredBundle {
  bundle: FlagBundle;
  at: number;
}

/** One exposure, as it goes over the wire. */
interface ExposureRow {
  key: string;
  /** The assigned arm, or null on a plain gate. */
  variant: string | null;
  /** The on/off reading, so a gate's exposure is still analysable. */
  value: boolean;
  /** Which step decided it — `rule` vs `default` is control-group membership. */
  reason: string;
  /** Milliseconds since epoch, stamped when the flag was read, not when sent. */
  at: number;
}

/**
 * Client-side feature flags.
 *
 * Reached as `fg.flags` on an initialised SDK, or as the `flags` singleton
 * export. Nothing on this class throws.
 */
export class FeatureFlags extends FlowGridTransport {
  /** The compiled bundle. Null until the first fetch or storage read succeeds. */
  private bundle: FlagBundle | null = null;

  /**
   * Which copy of the bundle is in `this.bundle` — the delivery half of an
   * answer's explanation. Null while there is no bundle at all, which is the
   * state that makes every read a `fallback`.
   */
  private bundleSource: 'network' | 'persistence' | null = null;

  /** See {@link FlagLookupPolicy}. */
  private lookupPolicyMode: FlagLookupPolicy = 'persistenceUntilNetworkSuccess';

  /** See {@link FlagEngineOptions.persistenceTtlMs}. */
  private persistenceTtlMs: number = STORAGE_MAX_AGE_MS;

  /**
   * True once a bundle's `delivery` block has set the policy — i.e. the customer
   * chose one in the FlowGrid dashboard.
   *
   * Reported by {@link lookupPolicySource} so that "why is my
   * `Flowgrid.init({ flags: … })` being ignored?" is answerable from the console
   * instead of from our support inbox.
   */
  private policyFromDashboard = false;

  /**
   * The path core answered `204 No Content` for — flag delivery is off here.
   *
   * Only ever set on a first load, when there was no cached bundle to check the
   * path against locally. Held as the path rather than a boolean because a
   * single-page app navigates without reloading: the block belongs to that page,
   * not to the session.
   */
  private serverBlockedPath: string | null = null;

  /** What we know about this visitor. Seeded from the SDK, extended by `identify`. */
  private context: FlagContext = {};

  /**
   * Server-resolved answers for flags whose rules can't ship to a browser.
   * These win over local evaluation, because locally we can't even see the rule.
   *
   * Full decisions rather than booleans: a private flag can be an experiment
   * too, and a resolve response that dropped the arm would leave `variant()`
   * answering null for exactly the flags whose targeting is most deliberate.
   */
  private resolved: Record<string, FlagDecision> = {};

  /** See {@link FlagEngineOptions.declare}. */
  private declared: NormalisedDeclaration[] = [];

  /** Keys read that this bundle doesn't contain — asked about, then reported. */
  private unknown = new Set<string>();
  private resolveTimer: ReturnType<typeof setTimeout> | null = null;
  private resolveInFlight = false;

  private listeners = new Set<() => void>();

  /** Answers forced for testing. Checked before anything else, never counted. */
  private overrides: FlagOverrides = {};
  private allowUrlOverrides = false;

  /**
   * Evaluation counts for this session, aggregated in memory and reported once
   * on pagehide. Counting here rather than sending per read is the only reason a
   * flag is safe to call inside a render loop.
   */
  private usage: Record<string, { on: number; off: number; unknown: number }> = {};
  private usageSampled = Math.random() < USAGE_SAMPLE_RATE;
  private usageHooked = false;

  /**
   * Exposures already reported this session, as `flag:arm`.
   *
   * Deduped on the arm rather than the flag, so a visitor whose assignment
   * changes mid-session — they logged in, and percentage rollouts bucket on the
   * most specific identity available — reports the new arm. An exposure that
   * silently stayed on the old one would attribute their behaviour to a variant
   * they stopped seeing.
   */
  private exposed = new Set<string>();

  /** Exposures collected but not yet sent. */
  private exposureQueue: ExposureRow[] = [];
  private exposureTimer: ReturnType<typeof setTimeout> | null = null;

  private readyPromise: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;
  private readyState = false;

  // --------------------------------------------------------------------------
  // Lifecycle
  // --------------------------------------------------------------------------

  /**
   * Load this site's flags.
   *
   * Called for you by `FlowGrid.init()`. Safe to call again — a second call
   * refreshes the bundle rather than starting a second engine. (Named `load`
   * rather than `init` because `FlowGridTransport.init` is the generic event
   * sender every feature inherits.)
   *
   * Best-effort throughout: a failed fetch leaves whatever was in localStorage,
   * and an empty cache leaves every flag on its caller-supplied default. A site
   * with no flags must not leave `useFlag()` spinning forever, so `ready()`
   * settles either way.
   */
  /**
   * Set how answers are fetched and kept. Safe to call before `load()`.
   *
   * `Flowgrid.init({ flags: { … } })` calls this for you; it is public so an app
   * that constructs the engine itself, or wants to tighten the policy on one
   * route, does not have to reach through init to do it.
   *
   * Changing the policy does not re-run a load that has already happened — it
   * describes the next one. Only the keys you pass move, so
   * `configure({ persistenceTtlMs: 0 })` leaves the policy alone.
   */
  configure(options: FlagEngineOptions): void {
    if (options.lookupPolicy) this.lookupPolicyMode = options.lookupPolicy;
    if (typeof options.persistenceTtlMs === 'number' && options.persistenceTtlMs >= 0) {
      this.persistenceTtlMs = options.persistenceTtlMs;
    }
    if (options.declare) this.declared = normaliseDeclarations(options.declare);
    if (typeof options.allowUrlOverrides === 'boolean') this.allowUrlOverrides = options.allowUrlOverrides;
  }

  /**
   * Send the declared keys this bundle lacks. Only against a bundle fetched this
   * load — a persisted copy may simply predate a flag the dashboard already has.
   */
  private declareMissing(): void {
    if (this.bundleSource !== 'network') return;

    sendDeclarations(this.endpoint, this.webId, missingFrom(this.bundle, this.declared));
  }

  /** The policy in force. Reported alongside `explain()` when debugging. */
  get lookupPolicy(): FlagLookupPolicy {
    return this.lookupPolicyMode;
  }

  /**
   * Who chose the policy in force — `'dashboard'` or `'client'`.
   *
   * The dashboard wins when the site has configured delivery there, and silently
   * overriding an app's own init config is exactly the kind of thing that costs
   * somebody an afternoon. This makes it a one-line answer.
   */
  get lookupPolicySource(): 'dashboard' | 'client' {
    return this.policyFromDashboard ? 'dashboard' : 'client';
  }

  /**
   * Adopt a bundle's `delivery` block, if it carries one.
   *
   * Absent is the common case and means nobody has chosen in the dashboard —
   * so the app's own `configure()` / init config stays in charge. Present is the
   * dashboard speaking, and it wins.
   *
   * Values are checked rather than trusted. The bundle is fetched over the
   * network and parsed from JSON; a malformed policy applied blindly would
   * change how a customer's site caches flags on the strength of a typo.
   */
  private applyBundleDelivery(delivery: FlagDelivery | undefined): void {
    if (!delivery) return;

    if (LOOKUP_POLICIES.includes(delivery.lookup_policy)) {
      this.lookupPolicyMode = delivery.lookup_policy;
      this.policyFromDashboard = true;
    }
    const ttl = delivery.persistence_ttl_ms;
    if (typeof ttl === 'number' && Number.isFinite(ttl) && ttl >= 0) {
      this.persistenceTtlMs = ttl;
    }
  }

  async load(context?: FlagInputContext, options?: FlagEngineOptions): Promise<void> {
    // Applied before anything reads them: the policy decides whether the very
    // next line is allowed to touch storage.
    if (options) this.configure(options);

    // Seed what the SDK can work out for itself before the caller's context, so
    // an explicitly passed value still wins. This is what makes a percentage
    // rollout split anonymous traffic correctly with no work from the app — see
    // `resolveVisitorId` — and what stops `environment` and `sdk_version` rules
    // from being rules that compile and then never match.
    this.seedContext();
    if (context) this.context = { ...this.context, ...context };
    this.overrides = loadOverrides(this.webId, { allowUrl: this.allowUrlOverrides });

    // The dashboard's setting, as of the last bundle we stored, decides how that
    // stored bundle may now be used.
    //
    // It reads circular and is not: this is how `Cache-Control` works. The stored
    // copy carries the policy that was in force when it was stored, and that
    // policy governs this load; the fetch below then carries the current one for
    // the next. A first-ever visitor has nothing stored, so nothing applies and
    // they simply wait for the fetch.
    this.applyBundleDelivery(this.peekStoredDelivery());

    // Storage first, so a returning visitor evaluates correctly on the very
    // first render instead of flashing the default for a network round trip.
    // Under the other two policies the app has said it would rather show its own
    // default than a stored answer, so nothing is read here and the fallback
    // stands until the fetch settles.
    if (!this.bundle && this.lookupPolicyMode === 'persistenceUntilNetworkSuccess') {
      this.adoptStoredBundle();
    }

    // Nothing to ask for on a path this site has switched flags off for. The
    // cheapest response is the request that never happens: once a bundle is
    // cached, every later load on a blocked path costs zero bytes and puts
    // nothing in the site's own network tab.
    const path = typeof location !== 'undefined' ? location.pathname : null;
    if (this.bundle && bundleBlocksPath(this.bundle, path)) {
      this.markReady();
      this.emit();
      return;
    }

    try {
      // `p` goes only on a first load. With no cached bundle we cannot know
      // whether this path is blocked, so core decides and answers 204 if it is.
      // Once there is a bundle that check happens locally and the URL goes back
      // to one-per-site — which is what keeps the shared cache in front of this
      // endpoint doing the work.
      const query = `web_id=${encodeURIComponent(this.webId)}${
        !this.bundle && path ? `&p=${encodeURIComponent(path)}` : ''
      }`;
      const res = await fetch(`${this.endpoint}/api/v1/flags?${query}`);

      if (res.status === 204) {
        // Core says delivery is off here. Remember the path for the reads that
        // follow, and deliberately leave the stored bundle alone: the visitor
        // may navigate from here to an unblocked page in the same session and
        // must still have their flags when they get there.
        this.serverBlockedPath = path;
      } else {
        const json = await res.json();
        if (json?.ok && json.data?.flags) {
          this.bundle = json.data as FlagBundle;
          this.bundleSource = 'network';
          // Before the write, not after: if the dashboard has just switched this
          // site to `networkOnly`, the promise is that nothing is stored — so
          // the write must already know that, and anything left from an earlier
          // policy has to go.
          this.applyBundleDelivery(this.bundle.delivery);
          if (this.lookupPolicyMode === 'networkOnly') this.clearStoredBundle();
          else this.writeStoredBundle(this.bundle);
        }
      }
    } catch {
      /* offline, blocked, or no flags — the cached bundle (or the defaults) stand */
    }

    // `networkFirst` holds persistence back as a safety net rather than a first
    // answer, so this is the moment it is allowed to catch. Outside the `catch`
    // on purpose: a 500 carrying `{ ok: false }` parses fine and throws nothing,
    // and "the network did not give us a bundle" is the condition that matters,
    // not "the network threw".
    if (!this.bundle && this.lookupPolicyMode === 'networkFirst') this.adoptStoredBundle();

    this.markReady();
    this.emit();
    this.declareMissing();

    // Anything read before the bundle arrived may now be answerable — and
    // anything still unknown may be a server-only flag.
    if (this.unknown.size > 0) this.scheduleResolve();
  }

  /**
   * Resolves once the first load has settled. Lets a UI hold a paint rather than
   * rendering the default and snapping to the real value a moment later.
   */
  ready(): Promise<void> {
    if (this.readyState) return Promise.resolve();
    if (!this.readyPromise) {
      this.readyPromise = new Promise<void>((resolve) => { this.readyResolve = resolve; });
    }
    return this.readyPromise;
  }

  /** True once flags have loaded (or definitively failed to). */
  get isReady(): boolean {
    return this.readyState;
  }

  private markReady(): void {
    if (this.readyState) return;
    this.readyState = true;
    this.readyResolve?.();
    this.readyResolve = null;
    if (!this.readyPromise) this.readyPromise = Promise.resolve();
  }

  // --------------------------------------------------------------------------
  // Reads
  // --------------------------------------------------------------------------

  /**
   * Is this flag on for the current visitor?
   *
   * @param key      The flag key from the dashboard.
   * @param fallback What to return when the flag is unknown here — because it
   *                 hasn't been created, the bundle hasn't loaded, or the
   *                 network is down. This is your code's own default, so pass
   *                 the honest one.
   *
   * Synchronous, never throws, safe to call in a render, safe to call before
   * `init()`.
   */
  on(key: string, fallback = false): boolean {
    return this.decide(key, fallback).value;
  }

  /**
   * Which variant is this visitor on?
   *
   * ```ts
   * if (flags.variant('checkout-copy') === 'urgent') { … }
   * ```
   *
   * `null` when the flag is a plain on/off gate, when it is switched off, when
   * this visitor fell outside the rollout, or when the flag doesn't exist here.
   * Null rather than a `'none'` sentinel: "which arm" either has an answer or it
   * has none, and a caller comparing against a magic string will eventually
   * forget to.
   *
   * Note the difference between `null` and the control arm — being *outside* an
   * experiment is not the same as being in its control group, and treating them
   * as the same is how a result stops meaning anything.
   *
   * Synchronous and never throws, exactly like `on()`.
   */
  variant(key: string): string | null {
    return this.decide(key, false).variant;
  }

  /**
   * The value this visitor's variant carries — the dynamic-config read.
   *
   * ```ts
   * const { retries } = flags.get('http-config', { retries: 3 });
   * const heading     = flags.get('hero-copy', 'Get started');
   * ```
   *
   * `fallback` is your code's own default and it covers every way there can be
   * no value: the flag doesn't exist here, it is switched off, it is a plain
   * gate, the visitor is outside the rollout, or the arm they're on carries no
   * value. Same contract as `on(key, fallback)` — you always get something
   * usable, never an exception and never a hang.
   *
   * The type comes from your fallback, so `get('http-config', { retries: 3 })`
   * is typed without a type argument.
   */
  get<T extends FlagVariantValue>(key: string, fallback: T): T {
    const payload = this.decide(key, false).payload;
    return payload === undefined ? fallback : (payload as T);
  }

  /**
   * The full answer plus why — value, arm, arm's value, and which step decided.
   *
   * For debugging a flag that isn't doing what you expect, and for logging an
   * assignment yourself if you want to. Does **not** count toward the usage
   * digest: asking why is not the same as reading the flag, and a debug call in
   * a loop should not distort the numbers that decide whether a flag is safe to
   * delete.
   */
  explain(key: string, fallback = false): FlagReading {
    return this.decide(key, fallback, { count: false });
  }

  /**
   * The one read path. `on`, `variant`, `get` and `explain` are all views of it,
   * so a rule about blocked routes or server-resolved answers is written once
   * and cannot be true of one read and false of another.
   */
  private decide(
    key: string,
    fallback: boolean,
    options?: { count?: boolean },
  ): FlagReading {
    const shouldCount = options?.count !== false;
    const unanswered: FlagReading = {
      value: fallback,
      variant: null,
      reason: 'unknown_flag',
      source: 'fallback',
    };
    if (!key) return unanswered;

    // The site switched flags off for this route in its FlowGrid tracking
    // settings. Answer exactly as an unreachable FlowGrid would — the caller's
    // own default — and record nothing: a page that opted out of flag delivery
    // has not agreed to be told which flags its visitors read.
    if (this.pathIsBlocked()) return unanswered;

    // A forced answer wins over everything, and is not a read worth counting:
    // a developer clicking through an arm is not a participant.
    const forced = this.overrides[key];
    if (forced !== undefined) {
      return { ...overrideDecision(this.bundle?.flags?.[key], forced), source: 'override' };
    }

    // A server-resolved answer wins: those are flags whose rules were withheld
    // from this client on purpose, so local evaluation cannot see them.
    const resolved = this.resolved[key];
    if (resolved) {
      if (shouldCount) this.count(key, resolved.value ? 'on' : 'off');
      return { ...resolved, source: 'resolve' };
    }

    const flag = this.bundle?.flags?.[key];
    if (!flag) {
      if (shouldCount) this.count(key, 'unknown');
      this.noteUnknown(key);
      return unanswered;
    }

    const decision = decideFlag(this.bundle, key, this.context, fallback);
    if (shouldCount) {
      this.count(key, decision.value ? 'on' : 'off');
      // Only flags the customer asked to measure, and only when the flag really
      // decided this — `explain()` passes shouldCount: false, because asking why
      // is not being exposed to a feature.
      if (flag.x) this.noteExposure(key, decision);
    }
    // A flag was found, so there is a bundle — but read the field rather than
    // asserting it, because a source that can be wrong is worse than no source.
    return { ...decision, source: this.bundleSource ?? 'fallback' };
  }

  // --------------------------------------------------------------------------
  // Exposures
  // --------------------------------------------------------------------------

  /**
   * Record that this visitor was shown this flag's answer.
   *
   * This is the row that makes a flag measurable. The sampled usage digest says
   * whether a flag is still read anywhere — the question you answer before
   * deleting it. An exposure says *who* saw *which arm* and *when*, which is the
   * only thing a conversion can be joined to. Neither substitutes for the other,
   * which is why both exist and only one of them is sampled.
   *
   * Deduped per session per arm: a flag read inside a render loop must produce
   * one row, not one per frame, and a hundred components asking the same
   * question are one exposure.
   */
  private noteExposure(key: string, decision: FlagDecision): void {
    // An unknown flag is not an exposure. Nothing was served.
    if (decision.reason === 'unknown_flag') return;

    const fingerprint = `${key}:${decision.variant ?? (decision.value ? 'on' : 'off')}`;
    if (this.exposed.has(fingerprint)) return;
    if (this.exposed.size >= MAX_SESSION_EXPOSURES) return;
    this.exposed.add(fingerprint);

    this.exposureQueue.push({
      key,
      variant: decision.variant,
      value: decision.value,
      reason: decision.reason,
      // Stamped at read time, not send time: the debounce would otherwise
      // reorder an exposure past the conversion it is meant to explain.
      at: Date.now(),
    });

    this.hookUsageFlush();
    if (this.exposureTimer) return;
    this.exposureTimer = setTimeout(() => {
      this.exposureTimer = null;
      this.sendExposures();
    }, EXPOSURE_DEBOUNCE_MS);
  }

  /**
   * Send whatever exposures have collected.
   *
   * `beacon` is set when the page is going away, where a `fetch` would be
   * cancelled by the navigation — which is exactly when the last exposure of a
   * session would be lost, and the last one is usually the interesting one.
   */
  private sendExposures(options?: { beacon?: boolean }): void {
    const rows = this.exposureQueue;
    this.exposureQueue = [];
    if (rows.length === 0) return;

    const body = JSON.stringify({
      web_id: this.webId,
      visitor_id: this.context.visitorId ?? null,
      user_id: this.context.userId ?? null,
      exposures: rows,
    });
    const url = `${this.endpoint}/api/v1/flags/exposure`;

    try {
      if (options?.beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        return;
      }
      if (typeof fetch === 'undefined') return;
      // `keepalive` so an exposure sent moments before a navigation still goes.
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {
        /* telemetry is never worth an error path */
      });
    } catch {
      /* nor is a blocked beacon */
    }
  }

  /** Sugar for the `on(key) === false` branch, so a guard clause reads well. */
  off(key: string, fallback = false): boolean {
    return !this.on(key, fallback);
  }

  /**
   * Run this visitor's path for a flag. If the new path throws, the error is
   * reported against the flag and the old path runs instead; if the old path
   * throws, it is reported and rethrown. Sync or async.
   *
   * ```ts
   * const total = await flags.run('new-pricing', () => priceV2(cart), () => price(cart));
   * ```
   */
  run<T>(key: string, newPath: () => T, oldPath: () => T, fallback = false): T {
    return runFlagged(key, this.on(key, fallback), newPath, oldPath, (k, error) => this.reportError(k, error));
  }

  /**
   * Report an error thrown by the code a flag guards. It counts toward that
   * flag's error rate, for the arm this visitor is on — never a page-wide error.
   */
  reportError(key: string, error: unknown): void {
    const arm = this.variant(key) ?? (this.on(key) ? 'on' : 'off');

    this.init('error_event', toFlagErrorPayload(key, arm, error)).catch(() => {
      /* reporting an error must never become one */
    });
  }

  /**
   * Is flag delivery switched off for the page we are on?
   *
   * Read fresh each call rather than cached at load, because a single-page app
   * navigates without reloading — a cached answer would keep applying the old
   * page's decision for the rest of the session.
   */
  private pathIsBlocked(): boolean {
    if (typeof location === 'undefined') return false;
    // A 204 from core is the same answer as a local match, and it is the only
    // answer available on a first load — there was no bundle to check against.
    if (this.serverBlockedPath !== null && this.serverBlockedPath === location.pathname) return true;
    return bundleBlocksPath(this.bundle, location.pathname);
  }

  /**
   * Every flag this client can see, evaluated. Handy for debugging and for
   * shipping the whole set into a server-rendered page; not the read path.
   *
   * Empty on a route where the site has switched flag delivery off, so a caller
   * spreading this over its own defaults cannot resurrect a blocked flag.
   */
  all(): Record<string, boolean> {
    if (this.pathIsBlocked()) return {};
    const out: Record<string, boolean> = {};
    for (const key of Object.keys(this.bundle?.flags ?? {})) {
      out[key] = evaluateFlag(this.bundle, key, this.context, false);
    }
    for (const [key, decision] of Object.entries(this.resolved)) out[key] = decision.value;
    for (const [key, forced] of Object.entries(this.overrides)) {
      out[key] = overrideDecision(this.bundle?.flags?.[key], forced).value;
    }
    return out;
  }

  /**
   * Every visible flag as a full decision — value, arm and arm's value.
   *
   * The shape to hand into a server-rendered page when the app uses variants:
   * `all()` flattens to booleans and would drop the arm on the way through.
   * Empty on a route where the site has switched flag delivery off, same as
   * `all()`.
   */
  allDecisions(): Record<string, FlagDecision> {
    if (this.pathIsBlocked()) return {};
    const out: Record<string, FlagDecision> = {};
    for (const key of Object.keys(this.bundle?.flags ?? {})) {
      out[key] = decideFlag(this.bundle, key, this.context, false);
    }
    for (const [key, decision] of Object.entries(this.resolved)) out[key] = decision;
    for (const [key, forced] of Object.entries(this.overrides)) {
      out[key] = overrideDecision(this.bundle?.flags?.[key], forced);
    }
    return out;
  }

  /**
   * Force answers while you test, for this tab:
   *
   * ```ts
   * flags.override({ 'checkout-copy': 'urgent', 'new-nav': true });
   * flags.override({ 'checkout-copy': null }); // back to the real answer
   * ```
   *
   * Forced reads are never counted or reported as exposures, and
   * `explain(key).source` says `override`, so nobody mistakes one for a rule.
   */
  override(next: Record<string, FlagOverride | null>): void {
    const merged: FlagOverrides = { ...this.overrides };
    for (const [key, value] of Object.entries(next)) {
      if (value === null) delete merged[key];
      else merged[key] = value;
    }
    this.overrides = merged;
    writeStoredOverrides(this.webId, merged);
    this.emit();
  }

  /** Drop every forced answer for this tab. */
  clearOverrides(): void {
    if (Object.keys(this.overrides).length === 0) return;
    this.overrides = {};
    writeStoredOverrides(this.webId, {});
    this.emit();
  }

  /** What is being forced right now — for a debug panel or a test assertion. */
  getOverrides(): Readonly<FlagOverrides> {
    return this.overrides;
  }

  /**
   * Subscribe to changes — a bundle arriving, a server resolution landing, or
   * `identify()` changing who the visitor is. Returns an unsubscribe function.
   *
   * This is what the framework bindings use to re-render.
   */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private emit(): void {
    for (const listener of this.listeners) {
      // A subscriber that throws is that subscriber's problem, not ours.
      try { listener(); } catch { /* ignore */ }
    }
  }

  /**
   * One flag as a subscribable store — the framework-agnostic primitive.
   *
   * Every UI framework wants the same two things from a reactive value: read it
   * now, and tell me when it changes. This returns exactly that, in the shape
   * any reactive framework can consume:
   *
   *   subscribe(listener) → unsubscribe    (listener is called immediately)
   *
   * Which is why FlowGrid does not need a binding per framework to be usable
   * from one. Four lines adopt it anywhere:
   *
   * ```ts
   * // Solid
   * const [on, setOn] = createSignal(false);
   * onCleanup(fg.flags.store('new-checkout').subscribe(setOn));
   *
   * // Angular
   * readonly on = toSignal(from(fg.flags.store('new-checkout')), { initialValue: false });
   *
   * // Vanilla / Alpine / Lit / anything
   * fg.flags.store('new-checkout').subscribe((on) => el.hidden = !on);
   * ```
   *
   * The shipped React binding is a convenience over this, not
   * privileged access — they use the same public surface you would.
   */
  store(key: string, fallback = false): { subscribe(listener: (value: boolean) => void): () => void } {
    return {
      subscribe: (listener: (value: boolean) => void) => {
        // Called immediately, per the store contract: a subscriber must never
        // have to render a placeholder waiting for a first value it could
        // already have had.
        let last = this.on(key, fallback);
        listener(last);

        return this.onChange(() => {
          const next = this.on(key, fallback);
          // Only notify on an actual change — a bundle refresh that changes
          // nothing must not re-render the app.
          if (next !== last) {
            last = next;
            listener(next);
          }
        });
      },
    };
  }

  // --------------------------------------------------------------------------
  // Context
  // --------------------------------------------------------------------------

  /**
   * Tell the flags who this visitor is.
   *
   * Called for you when the app calls `fg.identify(...)`. Merges rather than
   * replaces, re-evaluates immediately, and re-asks the server about any
   * server-only flags — targeting a specific user can't work until the SDK knows
   * which user it is.
   */
  identify(context: FlagInputContext): void {
    const before = JSON.stringify(this.context);
    this.context = {
      ...this.context,
      ...context,
      attributes: { ...(this.context.attributes ?? {}), ...(context.attributes ?? {}) },
    };
    if (JSON.stringify(this.context) === before) return;

    // Identity changed: previously resolved answers were for a different person.
    const previouslyResolved = Object.keys(this.resolved);
    this.resolved = {};
    for (const key of previouslyResolved) this.unknown.add(key);

    // And their rollout bucket may have moved with them — percentage rules
    // bucket on the most specific identity available, so logging in can change
    // which arm someone is on. The dedupe key already includes the arm, so the
    // new one reports and the old one is not re-sent.

    this.emit();
    if (this.unknown.size > 0) this.scheduleResolve();
  }

  /** The context flags are currently evaluated against. */
  getContext(): FlagContext {
    return { ...this.context };
  }

  /**
   * This visitor's persisted FlowGrid id — the same one every other feature
   * sends, resolved the same way (identity manager first, constructor value
   * otherwise).
   *
   * Percentage rollouts bucket on identity. With only `userId` available, an
   * anonymous visitor falls through to `webId`, which is identical for every
   * visitor of a site — so "roll out to 10%" would be on for all of them or none
   * of them. Reading the visitor id here fixes that for every customer without
   * them passing anything.
   *
   * Returns '' when there is no identity to read (a worker, a server runtime,
   * storage disabled). The cascade then behaves as it did before.
   */
  private resolveVisitorId(): string {
    try {
      return (this.identityManager ? this.identityManager.getVisitorId() : this.visitorId) || '';
    } catch {
      return '';
    }
  }

  /**
   * Fill in the context fields the SDK can answer for itself.
   *
   * Three of them, and each one is a rule type the flag editor offers:
   *
   *   visitorId   — see `resolveVisitorId`. Percentage rollouts need it.
   *   environment — from the host we were loaded on (`flagEnvironment.ts`).
   *   sdkVersion  — from the build constant.
   *
   * `region` is deliberately absent: a browser cannot answer it honestly, so
   * `region` rules compile private and flowgrid-core fills the field in when it
   * resolves them server-side.
   *
   * Every write here is conditional on the field being empty, so an app that
   * passes its own value — a Next.js app that knows its real `VERCEL_ENV`, say —
   * is never overwritten by our guess.
   */
  private seedContext(): void {
    const visitorId = this.resolveVisitorId();
    if (visitorId && !this.context.visitorId) this.context.visitorId = visitorId;

    if (!this.context.environment) {
      const environment = detectEnvironment();
      if (environment) this.context.environment = environment;
    }

    if (!this.context.sdkVersion) this.context.sdkVersion = SDK_VERSION;
  }

  // --------------------------------------------------------------------------
  // Unknown keys → server resolution + dashboard discovery
  // --------------------------------------------------------------------------

  /**
   * A key was read that this bundle doesn't have. Two possibilities, and one
   * request distinguishes them: it is a flag whose rules were withheld from
   * browsers (the server can answer), or it doesn't exist (the dashboard should
   * offer to create it). Batched and deduped — a flag read in a render loop must
   * never become a request per read.
   */
  private noteUnknown(key: string): void {
    if (this.unknown.has(key) || key in this.resolved) return;
    this.unknown.add(key);
    if (this.readyState) this.scheduleResolve();
  }

  private scheduleResolve(): void {
    if (this.resolveTimer || this.resolveInFlight) return;
    this.resolveTimer = setTimeout(() => {
      this.resolveTimer = null;
      void this.resolveUnknown();
    }, RESOLVE_DEBOUNCE_MS);
  }

  private async resolveUnknown(): Promise<void> {
    const keys = Array.from(this.unknown);
    if (keys.length === 0 || typeof fetch === 'undefined') return;

    this.resolveInFlight = true;
    try {
      const res = await fetch(`${this.endpoint}/api/v1/flags/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ web_id: this.webId, keys, context: this.context }),
      });
      const json = await res.json();
      if (json?.ok && json.data?.flags) {
        // `decisions` carries the arm and its value; `flags` is the boolean-only
        // shape. Both are read because an SDK can outlive a deploy in either
        // direction — a new SDK against an older core sees only `flags`, and a
        // browser holding an old bundle should still get a usable answer.
        const decisions = (json.data.decisions ?? {}) as Record<string, FlagDecision>;
        const booleans = (json.data.flags ?? {}) as Record<string, boolean>;

        const next: Record<string, FlagDecision> = { ...this.resolved };
        for (const key of Object.keys(booleans)) {
          const decision = decisions[key];
          next[key] =
            decision && typeof decision.value === 'boolean'
              ? { ...decision, variant: decision.variant ?? null }
              : { value: booleans[key], variant: null, reason: 'rule' };
        }
        this.resolved = next;

        // Keys stay out of `unknown` once answered — including answered `false`,
        // which is a real answer, not a miss.
        for (const key of keys) this.unknown.delete(key);
        this.emit();
      }
    } catch {
      // Leave the keys queued; the next identify or init will retry. Until then
      // every read of them returns the caller's default, which is correct.
    } finally {
      this.resolveInFlight = false;
    }
  }

  // --------------------------------------------------------------------------
  // Usage reporting
  // --------------------------------------------------------------------------

  /** Tally one evaluation. Pure bookkeeping — no I/O, no allocation per read. */
  private count(key: string, outcome: 'on' | 'off' | 'unknown'): void {
    if (!this.usageSampled) return;
    const entry = this.usage[key] ?? (this.usage[key] = { on: 0, off: 0, unknown: 0 });
    entry[outcome]++;
    this.hookUsageFlush();
  }

  /**
   * Arrange for the digest to be sent when the page goes away.
   *
   * `pagehide` rather than `unload`: `unload` breaks the bfcache and doesn't
   * fire reliably on mobile, which is where sessions end. `visibilitychange` to
   * hidden covers a tab switch that never comes back — the two together are what
   * actually catch the end of a session.
   */
  private hookUsageFlush(): void {
    if (this.usageHooked || typeof window === 'undefined') return;
    this.usageHooked = true;

    const send = () => {
      this.sendUsageDigest();
      // Exposures ride the same hook rather than installing a second one: they
      // end for the same reason at the same moment, and one listener is one
      // thing to get wrong.
      if (this.exposureTimer) {
        clearTimeout(this.exposureTimer);
        this.exposureTimer = null;
      }
      this.sendExposures({ beacon: true });
    };
    window.addEventListener('pagehide', send);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') send();
    });
  }

  /**
   * Report the session's counts, once.
   *
   * `sendBeacon` because the page is unloading: a `fetch` here is cancelled by
   * the navigation, which is exactly when the data would be lost. The digest is
   * fire-and-forget and unauthenticated — the server clamps the counts.
   */
  private sendUsageDigest(): void {
    const usage = this.usage;
    this.usage = {};
    if (Object.keys(usage).length === 0) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;

    try {
      const body = JSON.stringify({ web_id: this.webId, usage });
      navigator.sendBeacon(
        `${this.endpoint}/api/v1/flags/usage`,
        new Blob([body], { type: 'application/json' }),
      );
    } catch {
      /* telemetry is never worth an error path */
    }
  }

  // --------------------------------------------------------------------------
  // Storage
  // --------------------------------------------------------------------------

  /**
   * Take the persisted bundle as the current answer, if there is a usable one.
   *
   * The only place `bundleSource` is set to `persistence`, so an answer can
   * never claim to have come from storage without having actually been read from
   * it — which is the whole value of reporting a source at all.
   */
  private adoptStoredBundle(): void {
    const stored = this.readStoredBundle();
    if (!stored) return;
    this.bundle = stored;
    this.bundleSource = 'persistence';
  }

  /**
   * Read only the `delivery` block out of the stored record.
   *
   * Deliberately ignores both the lookup policy and the TTL, which the two reads
   * either side of it enforce. Neither applies here: the policy is the thing
   * being determined, and a setting is not an answer — a superseded one is still
   * the last thing the customer chose, and the fetch a moment later corrects it.
   * Reading answers under those rules is `readStoredBundle`'s job.
   */
  private peekStoredDelivery(): FlagDelivery | undefined {
    try {
      if (typeof localStorage === 'undefined') return undefined;
      const raw = localStorage.getItem(STORAGE_KEY(this.webId));
      if (!raw) return undefined;
      return (JSON.parse(raw) as StoredBundle)?.bundle?.delivery;
    } catch {
      return undefined;
    }
  }

  /** Forget the persisted bundle. Used when a policy stops permitting one. */
  private clearStoredBundle(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.removeItem(STORAGE_KEY(this.webId));
    } catch {
      /* private mode, disabled storage — nothing to forget anyway */
    }
  }

  private readStoredBundle(): FlagBundle | null {
    // Under `networkOnly` the store is not ours to read even if something is in
    // it — a bundle left by an earlier page load under a different policy must
    // not leak back in.
    if (this.lookupPolicyMode === 'networkOnly') return null;
    try {
      if (typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(STORAGE_KEY(this.webId));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredBundle;
      if (!parsed?.bundle?.flags) return null;
      // A very old bundle is likelier to be wrong than useful — a flag turned
      // off a week ago must not come back to life on a stale cache.
      if (Date.now() - (parsed.at ?? 0) > this.persistenceTtlMs) return null;
      return parsed.bundle;
    } catch {
      return null;
    }
  }

  private writeStoredBundle(bundle: FlagBundle): void {
    // `networkOnly` means the answers do not outlive the tab. Writing here and
    // simply declining to read it back would leave a bundle on the visitor's
    // machine that nothing intends to use.
    if (this.lookupPolicyMode === 'networkOnly') return;
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(STORAGE_KEY(this.webId), JSON.stringify({ bundle, at: Date.now() } as StoredBundle));
    } catch {
      /* private mode, quota, disabled storage — evaluation still works in memory */
    }
  }
}
